import { repo } from '@/db/repo'
import { ApiError, type Song } from '@/types'
import { sortBy } from '@/lib/utils'
import { required, type RequestContext, type Route } from '@/services/api/router'

/**
 * Public, read-only API consumed by the main MyMDV church app.
 *
 * Only songs with `isPublic === true` ("Expose to Public App" in the song
 * editor) are ever visible here, and lyrics/chord sheets are deliberately
 * omitted from the list endpoint so a public feed cannot leak a whole
 * copyrighted catalogue in one request.
 */

export interface PublicSong {
  id: string
  title: string
  artist: string
  author?: string
  ccli_number?: string
  bpm: number
  original_key: string
  meter?: string
  themes: string[]
  youtube_url?: string
  last_scheduled_date?: string
  times_scheduled: number
  updated_at: string
}

function toPublicSong(song: Song): PublicSong {
  return {
    id: song.id,
    title: song.title,
    artist: song.artist,
    author: song.author,
    ccli_number: song.ccliNumber,
    bpm: song.bpm,
    original_key: song.originalKey,
    meter: song.meter,
    themes: song.themes,
    youtube_url: song.youtubeUrl,
    last_scheduled_date: song.lastScheduledDate,
    times_scheduled: song.timesScheduled,
    updated_at: song.updatedAt,
  }
}

/**
 * Enforces the two Admin -> Public API switches: the master enable flag, and
 * the optional `X-API-Key` requirement.
 */
async function assertPublicAccess(ctx: RequestContext): Promise<void> {
  const settings = await repo.get('settings', 'app-settings')
  if (settings && !settings.publicApiEnabled) {
    throw new ApiError(503, 'public_api_disabled', 'The public API is currently disabled')
  }
  if (!settings?.publicApiRequiresKey) return

  const presented =
    ctx.headers['x-api-key'] ?? ctx.headers['X-API-Key'] ?? ctx.query.get('api_key') ?? ''
  if (!presented) {
    throw new ApiError(401, 'missing_api_key', 'Send your key in the X-API-Key header')
  }
  const keys = await repo.all('apiKeys')
  const match = keys.find((k) => k.key === presented && !k.revoked)
  if (!match) throw new ApiError(403, 'invalid_api_key', 'That API key is unknown or revoked')
  await repo.patch('apiKeys', match.id, { lastUsedAt: new Date().toISOString() })
}

export const publicRoutes: Route[] = [
  {
    method: 'GET',
    pattern: '/public/health',
    visibility: 'public',
    description: 'Liveness probe for the main app. No key required.',
    handler: async () => {
      const settings = await repo.get('settings', 'app-settings')
      const songs = await repo.all('songs')
      return {
        status: 'ok',
        service: 'mymdv-services',
        public_api_enabled: settings?.publicApiEnabled ?? true,
        requires_api_key: settings?.publicApiRequiresKey ?? false,
        public_song_count: songs.filter((s) => s.isPublic).length,
      }
    },
  },
  {
    method: 'GET',
    pattern: '/public/songs',
    visibility: 'public',
    description: 'Songs flagged "Expose to Public App". Supports ?q= and ?theme=',
    handler: async (ctx) => {
      await assertPublicAccess(ctx)
      const q = ctx.query.get('q')?.toLowerCase().trim()
      const theme = ctx.query.get('theme')
      const limit = Number(ctx.query.get('limit') ?? '0')

      let songs = (await repo.all('songs')).filter((s) => s.isPublic)
      if (q) songs = songs.filter((s) => `${s.title} ${s.artist}`.toLowerCase().includes(q))
      if (theme) songs = songs.filter((s) => s.themes.includes(theme))
      songs = sortBy(songs, (s) => s.title.toLowerCase())
      if (limit > 0) songs = songs.slice(0, limit)
      return songs.map(toPublicSong)
    },
  },
  {
    method: 'GET',
    pattern: '/public/songs/:songId',
    visibility: 'public',
    description: 'One public song, including lyrics and chord sheet.',
    handler: async (ctx) => {
      await assertPublicAccess(ctx)
      const song = required(await repo.get('songs', ctx.params.songId), 'Song', ctx.params.songId)
      if (!song.isPublic) {
        throw new ApiError(403, 'not_public', 'That song is not exposed to the public app')
      }
      const arrangements = await repo.byIndex('arrangements', 'by-song', song.id)
      return {
        ...toPublicSong(song),
        lyrics: song.lyrics,
        chord_sheet: song.chordSheet,
        copyright: song.copyright,
        arrangements: arrangements.map((a) => ({
          id: a.id,
          name: a.name,
          key: a.key,
          bpm: a.bpm,
          sequence: a.sequence,
          length_seconds: a.lengthSeconds,
          is_default: a.isDefault,
        })),
      }
    },
  },
  {
    method: 'GET',
    pattern: '/public/arrangements',
    visibility: 'public',
    description: 'Arrangements belonging to public songs. Supports ?songId=',
    handler: async (ctx) => {
      await assertPublicAccess(ctx)
      const songId = ctx.query.get('songId')
      const publicSongIds = new Set(
        (await repo.all('songs')).filter((s) => s.isPublic).map((s) => s.id),
      )
      const arrangements = songId
        ? await repo.byIndex('arrangements', 'by-song', songId)
        : await repo.all('arrangements')
      return arrangements
        .filter((a) => publicSongIds.has(a.songId))
        .map((a) => ({
          id: a.id,
          song_id: a.songId,
          name: a.name,
          key: a.key,
          bpm: a.bpm,
          sequence: a.sequence,
          length_seconds: a.lengthSeconds,
          is_default: a.isDefault,
        }))
    },
  },
]

/**
 * Builds the static JSON snapshot uploaded to
 * `/api/v1/public/songs.json` on BlueHost, so cross-origin callers get the
 * feed straight from Apache without the app being open in a tab.
 */
export async function buildPublicSnapshot(): Promise<{
  songs: unknown
  arrangements: unknown
}> {
  const songs = sortBy(
    (await repo.all('songs')).filter((s) => s.isPublic),
    (s) => s.title.toLowerCase(),
  )
  const publicIds = new Set(songs.map((s) => s.id))
  const arrangements = (await repo.all('arrangements')).filter((a) => publicIds.has(a.songId))
  const generatedAt = new Date().toISOString()

  return {
    songs: {
      meta: {
        endpoint: '/api/v1/public/songs',
        version: 'v1',
        generated_at: generatedAt,
        count: songs.length,
      },
      data: songs.map(toPublicSong),
    },
    arrangements: {
      meta: {
        endpoint: '/api/v1/public/arrangements',
        version: 'v1',
        generated_at: generatedAt,
        count: arrangements.length,
      },
      data: arrangements.map((a) => ({
        id: a.id,
        song_id: a.songId,
        name: a.name,
        key: a.key,
        bpm: a.bpm,
        sequence: a.sequence,
        length_seconds: a.lengthSeconds,
        is_default: a.isDefault,
      })),
    },
  }
}
